// Shared pack logo tile.
//
// Resolves to (in order):
//   1. profile.logo if it's already a URL/path that the browser can load
//   2. /api/packs/:id/logo (backend resolver — built-in or uploaded)
//   3. Fallback per `fallback` prop:
//        - "initial" (default): single-letter monogram. Best for grids
//          where a wall of identical Tableau logos would lose the
//          "which pack is which" signal.
//        - "tableau": render the bundled Tableau wordmark. Hero
//          contexts (Monitor, Run detail header, Run history hero)
//          opt in via this prop — addresses the polish nit where a
//          freshly-AI-generated pack with no logo_url shows a generic
//          monogram on the most-watched surface in the app.
//
// Always renders inside a same-sized tile with neutral background so the
// list/grid stays aligned even when an image fails or transparent PNGs
// are dropped onto a saturated section background.

import { useMemo, useState } from "react";
import type { Profile } from "@/lib/types";

function isLikelyUrl(value?: string): boolean {
  if (!value) return false;
  return /^(https?:|\/|data:)/i.test(value);
}

interface PackLogoProps {
  profile: Pick<Profile, "id" | "name" | "logo">;
  size?: number;
  rounded?: number;
  background?: string;
  fallback?: "initial" | "tableau";
}

const TABLEAU_LOGO_PATH = "/assets/tableau-logo.png";

export function PackLogo({
  profile,
  size = 40,
  rounded = 10,
  background = "var(--cb-98, #F1F5F9)",
  fallback = "initial",
}: PackLogoProps): JSX.Element {
  const candidates = useMemo<string[]>(() => {
    const list: string[] = [];
    if (isLikelyUrl(profile.logo)) list.push(profile.logo);
    if (profile.id) list.push(`/api/packs/${encodeURIComponent(profile.id)}/logo`);
    if (fallback === "tableau") list.push(TABLEAU_LOGO_PATH);
    return list.filter(Boolean);
  }, [profile.id, profile.name, profile.logo, fallback]);

  const [idx, setIdx] = useState(0);
  const src = candidates[idx];
  const showImage = src !== undefined;

  const initial = (profile.name?.trim()?.[0] ?? "?").toUpperCase();

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: rounded,
        background,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        overflow: "hidden",
        position: "relative",
        // Subtle inner border keeps tile shape even when art is white-on-white.
        boxShadow: "inset 0 0 0 1px rgba(15,23,42,0.06)",
      }}
      aria-hidden={showImage ? undefined : true}
    >
      {showImage ? (
        <img
          src={src}
          alt=""
          width={size}
          height={size}
          style={{
            width: "100%",
            height: "100%",
            // `contain` preserves the brand mark inside the tile rather
            // than cropping. Most uploaded marks have transparent edges
            // and were getting clipped under `cover`.
            objectFit: "contain",
            padding: Math.max(2, Math.round(size * 0.08)),
            background: "transparent",
          }}
          onError={() => {
            // Try the next candidate; if none left, fall through to initial.
            setIdx((i) => (i + 1 < candidates.length ? i + 1 : candidates.length));
          }}
        />
      ) : (
        <span
          style={{
            fontSize: Math.round(size * 0.45),
            fontWeight: 700,
            color: "var(--ink-2, #475569)",
            letterSpacing: "-0.02em",
          }}
        >
          {initial}
        </span>
      )}
    </div>
  );
}
